import { useCallback, useEffect, useRef } from 'react'
import { CIMSSelectProps, LabeledValueType, Props } from '../types'
import { CIMSTag } from '../../CIMSTag'

/**
 * a customized {@link Props.tagRender|tagRender} callback for the CIMSSelect
 * component
 *
 * pulled out so deprecated selectors can implement it as needed
 *
 * @param options {@link CIMSSelectProps}
 */
export function useTagRender(
  options: Pick<CIMSSelectProps, 'value' | 'simple' | '__debug'>
) {
  const meta = useRef<TagRenderMetadataMap>(new Map())
  useEffect(() => {
    const { value, simple } = options
    if (!value) return
    if (simple) return
    const casted = value as LabeledValueType | LabeledValueType[]
    if (Array.isArray(casted)) {
      casted.forEach(({ value, deleted, tooltip }) => {
        meta.current.set(value, {
          deleted,
          tooltip,
        })
      })
    } else
      meta.current.set(casted.value, {
        deleted: casted.deleted,
        tooltip: casted.tooltip,
      })
  }, [options])
  useEffect(() => {
    if (options.__debug)
      console.log('useTagRender', { meta: meta.current, ...options })
  }, [options, meta])

  /**
   * this ugly type is due to the fact useCallback doesn't like optional types
   * and the function signature for `tagRender` is not exported from ant design
   *
   * basically it makes `tagRender` required
   */
  type TagRenderFn = Required<Pick<Props, 'tagRender'>>['tagRender']
  const callback = useCallback<TagRenderFn>(
    (props) => {
      const { deleted, tooltip: tip } = meta.current.get(props.value) ?? {}
      if (options?.__debug)
        console.log('useTagRender.callback', { deleted, tooltip: tip, props })
      let tooltip = tip
      if (deleted) tooltip = 'Inactive'
      return (
        <CIMSTag {...props} tooltip={tooltip} strikethrough={deleted}>
          {props.label}
        </CIMSTag>
      )
    },
    [options]
  )

  return callback
}

type TagRenderMetadataMap = Map<
  LabeledValueType['value'],
  Pick<LabeledValueType, 'deleted' | 'tooltip'>
>
